<template>
  <base-layout>
    <ul class="nav navbar-nav navbar-left" slot="toolbar_buttons">
    </ul>

    <!-- slot main -->

    <div class="col-xs-12 main" slot="main">
      <!-- components -->
      <div class="row ls-charts">
        <h1>Posting Instructions</h1>
        <div class="row">
          <div class="col-xs-12 doc-wrapper">
            <div class="box panel">
              <div class="panel-body">
                <h2>Authentication</h2>
                <div class="api-key-box">
                  <h4>Current API Key</h4>
                  <div class="api-key-box-content">
                    <FormulateInput type="text" v-model="apiKey" disabled />
                    <FormulateInput type="button" @click="copyAPIKey()"><i class="fa fa-copy"></i>&nbsp;Copy</FormulateInput>
                    <span v-if="copiedAPIKey" class="copied-feedback"></span>
                  </div>
                </div>


                <p>LeadScorz's authentication scheme uses a simple token-based HTTP Authentication. For clients to authenticate, the token key should be included in the <code>Authorization HTTP header</code> for all <b>API requests</b>. The key should be prefixed by the string literal "Token", with a whitespace between the two strings, like in the following example:</p>
                <pre><code>
Authorization: Token {{ apiKey }}
                </code></pre>
                <h3>Obtaining a Token</h3>
                <p>For the purposes of submitting leads to LeadScorz you can assume that a Token will not expire. In order to get your Token or refresh the current one, you must do a <code>POST</code> request to the API login endpoint: <code>https://backend.leadscorz.com/api/v1/auth/login/</code></p>
                <p>The login endpoint expects two parameters:</p>
                <ul>
                  <li><code>email</code>: The email address used to setup your account.</li>
                  <li><code>password</code>: The password you used to setup your account.</li>
                </ul>
                <p>If the credentials are valid a token will be returned in a JSON response:</p>
                <pre><code>
{ "key": "{{ apiKey }}" }
                </code></pre>
                <p>Otherwise an <code>HTTP 400 Bad Request</code> will be returned in a JSON response with an error message.</p>
                <pre><code>
{ "non_field_errors": [ "Unable to log in with provided credentials." ] }
                </code></pre>
                <h2>Submitting Leads</h2>
                <p>Submitting leads to LeadScorz is a simple as doing a <code>POST</code> request with the lead data. We expect data to be encoded in UTF-8 and we accept the following Content Types:</p>
                <ul>
                  <li><b>application/json</b></li>
                  <li><b>application/x-www-form-urlencoded</b></li>
                  <li><b>multipart/form-data</b></li>
                </ul>
                <p>Each campaign will have two available endpoints:</p>
                <ul>
                  <li><b>Production</b>: This is the URL that must be used to send live leads. </li>
                  <li><b>Test</b>: This URL must be used while doing tests. </li>
                </ul>
                <h2>Testing</h2>
                <p>All test requests will be handled like a live request, but the leads will not be delivered to the final customer. There are two ways of submitting test leads:</p>
                <ul>
                  <li>During setup the Production Endpoint must be used to send test leads. The Campaign will be in test mode until it is promoted to Production. All the Campaigns in Test Mode will have the label <span class="label">TEST</span> by their name in the Capaigns list.</li>
                  <li>Once the campaign is in Production Mode, test can be submitted using the <b>Test Endpoint</b>. The Test Endpoint is only available when the Campaign is in Production Mode. Any requests send to the Test Endpoint will be handled as tests.</li>
                </ul>
                <h2>Responses</h2>
                <p>All the responses will have the following format:</p>
                <pre><code>
{
    "id": "ab78f7e9-ee5f-46fc-85d7-10f9ca18617a",
    "lead": "e348d332-4077-4f31-a64e-b63ced4e962a"
    "status": "OK"
    "data": {
        "first_name": "Test",
        "last_name": "Tester"
    }
    "errors": []
}
                </code></pre>
                <ul>
                  <li><code>id</code>: This is the request ID in LeadScorz. Please use this ID to reference your request if needed.</li>
                  <li><code>lead</code>: If the request generated a lead in LeadScorz this field will contain the lead ID. Otherwise it will be empty (<code>""</code>).</li>
                  <li><code>status</code>: It will contain the status of the request when the response was generated. It can be:
                    <ul>
                      <li><code>ok</code>: The request was processed correctly without any errors.</li>
                      <li><code>test</code>: The request was processed correctly in test mode.</li>
                      <li><code>pending</code>: The request has not been fully processed yet.</li>
                      <li><code>error</code>: The request was processed but some errors were found. The <code>errors</code> field will contain a detailed list of all the errors.</li>
                    </ul></li>
                  <li><code>data</code>: It will contain the data received with the request.</li>
                  <li><code>errors</code>: This is a list with all the errors produced during the processing of the request. Each campaign may produce a different set of errors, so please refer to the documentation of each campaign.</li>
                </ul>
                <h3>Error Responses</h3>
                <p>While processing the request, the following errors can be produced:</p>
                <h4>REQUIRED_FIELD</h4>
                <h5>Cause:</h5>
                <ul>
                  <li>A field that is required is missing or empty.</li>
                </ul>
                <h5>Solution:</h5>
                <ul>
                  <li>Check the list of fields and submit all required fields.</li>
                </ul>
                <h5>Response Sample</h5>
                <pre><code>
{
    "code": "REQUIRED_FIELD",
    "field": "field_name",
    "message": "This field is required"
}
                </code></pre>
                <h4>INVALID_VALUE</h4>
                <h5>Cause:</h5>
                <ul>
                  <li>The value for a <b>required field</b> is invalid.</li>
                </ul>
                <h5>Solution:</h5>
                <ul>
                  <li>
                    Submit a valid value. Usually there is a problem with the format (ie. invalid phone number format in a phone field).
                  </li>
                  <li>
                    Please note that invalid values for optional fields will be ignored.
                  </li>
                </ul>
                <h5>Response Sample:</h5>
                <pre><code>
{
    "code": "INVALID_VALUE",
    "field": "field_name",
    "message": "The value 'example' is invalid for 'field_name'"
}
                </code></pre>
                <h3>HTTP Status Codes</h3>
                <ul>
                  <li>
                    Success: <code>HTTP 201 Created</code>
                  </li>
                  <li>
                    Error: <code>HTTP 400 Bad Request</code>
                  </li>
                </ul>
                <h3>Other Responses</h3>
                <p>While using our API there are other responses that may be produced. Usually all responses will have the appropiate HTTP status code. The most common ones are:</p>
                <ul>
                  <li><code>HTTP 401 Unauthorized</code>: This usually means that the <code>Authorization</code> header is not present or it is incorrect. Please remember that all requests to these endpoints must include the proper <code>Authorization</code> header with the API token.</li>
                  <li><code>HTTP 404 Not Found</code>: This usually means that the endpoint you are trying to reach does not exist. Please check the proper URL for each Campaign. If a Campaign is inactive a 404 will be returned if trying to submit leads to it.</li>
                  <li><code>HTTP 405 Method Not Allowed</code>: The endpoints described in this document only accept <code>POST</code> requests. Please check that you are not trying to do a <code>GET</code> or <code>PUT</code> request instead.</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </div>
    <div slot="modals">

    </div>
  </base-layout>
</template>

<script>
 /**
  * https://github.com/jbaysolutions/vue-grid-layout
  */
 import Vue from 'vue';

  import {BaseLayout} from 'components/layouts/';

 // Load the full build.
 var _ = require('lodash');

 export default {
   components: {
     BaseLayout,
   },
   data () {
     return {
       copiedAPIKey: false,
       apiKey: null,
     };
   },
   created () {
     this.apiKey = this.$store.state.apiToken;
   },
   methods: {
     copyAPIKey() {
       var self = this;
       navigator.clipboard.writeText(this.apiKey).then(function() {
         self.copiedAPIKey = true;
         setTimeout(function () {
           self.copiedAPIKey = false;
         }, 2000);
       });

     },
   }
 }
</script>

<style lang="scss">
 .main {
   overflow-y: auto;
 }

 .api-key-box {
   border-radius: 4px 4px 4px 4px;
   border: 1px solid #aaa;
   padding: 10px;
   margin-bottom: 20px;

   .api-key-box-content {
     display: flex;
     flex-direction: row;

     .formulate-input {
       margin-right: 5px;
     }
   }
 }

 .empty-msg {
   margin: 30px auto;
 }


 /* Leads Queue styles */
 h1 .btn {
   background-color: transparent;
   margin-top: -8px;
 }

 h1 input {
   background: #fff;
   border: 0px!important;
   padding: 0px;
   padding-top: 4px;
   margin-top: -4px;
 }

 .leads-queue {
   padding: 0px!important;
   border: 1px solid #999;
   border-bottom: 0px;
   margin: 0px!important;
   float: none;
 }

 .leads-queue .chart-wrapper {
   padding: 0px!important;
   height: 100%!important;
 }

 .endeless-table-container {
   padding: 10px;
 }

 .leads-queue-wrapper {
   padding: 0px;
   position: absolute;
   height: 100%;
 }

 .leads-queue.full-height {
   position: absolute;
   height: 100%;
   width: 100%;
 }

 #dashboard-canvas {
   display: flex;
   flex-direction: column;
 }


 .ls-leads-table {
   display: flex;
   padding: 0px;
   position: relative;
   margin: 0px!important;
 }

 .with-leads-table .ls-charts {
   height: calc(100vh - 112px);
 }

 .with-leads-table.with-leads-table-active .ls-charts {
   height: 100%;
   flex-grow: 1;
   flex-basis: 35%;
   padding-bottom: 20px;
 }

 .with-leads-table.with-leads-table-active .ls-leads-table {
   height: 100%;
   flex-grow: 1;
   flex-basis: 65%;
 }

 .doc-wrapper {
   padding-left: 30px!important;
   padding-right: 30px!important;
 }
 .panel-body {
   overflow: auto;
 }

 .ls-charts {
   overflow-y: auto!important;
 }

 .ls-charts h1 {
   margin-left: 10px;
   margin-right: 10px;
 }

 .ls-charts > h4 {
   margin-top: -20px;
   margin-left: 30px;
   margin-right: 30px;
   padding: 5px 10px;
   background-color: #f5f5f5;
   border: #e3e3e3;
   border-radius: 0px 0px 4px 4px;
 }

 #dashboard-canvas h1 form {
   display: inline-block;
   width: 100%;
   height: 20px;
 }

 #dashboard-canvas h1 input[type="text"] {
   background-color: #efefef;
   display: inline-block;
   width: 90%;
 }

 .vue-grid-placeholder {
   background-color: #333!important;
   border-radius: 4px 4px 4px 4px;
 }

 .copied-feedback {
   display: inline-block;
   color: #ff6666;
   font-weight: 700;
 }

 .copied-feedback::after {
   content: "copied!";
 }

 .dest-list {
   margin-bottom: 1rem;
 }
</style>



// WEBPACK FOOTER //
// CampaignsDocs.vue?651ffb7a